import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, BarChart3, CalendarDays, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";

function ChartsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("market");
  const [overview, setOverview] = useState({ markets: [], starline_markets: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    API.get("/charts/overview")
      .then((res) => setOverview(res.data || { markets: [], starline_markets: [] }))
      .catch(() => setOverview({ markets: [], starline_markets: [] }))
      .finally(() => setLoading(false));
  }, []);

  const activeItems = useMemo(
    () => (tab === "market" ? overview.markets || [] : overview.starline_markets || []),
    [overview, tab]
  );

  return (
    <div style={page}>
      <header style={header}>
        <button type="button" style={backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1 style={title}>Charts</h1>
        <div style={headerSpacer} />
      </header>

      <main style={main}>
        <section style={tabWrap}>
          <button type="button" style={tab === "market" ? activeTab : tabButton} onClick={() => setTab("market")}>
            <BarChart3 size={18} /> Market
          </button>
          <button type="button" style={tab === "starline" ? activeTab : tabButton} onClick={() => setTab("starline")}>
            <Star size={18} /> Starline
          </button>
        </section>

        {activeItems.map((item) => (
          <section key={item.id} style={card}>
            <div style={marketName}>{item.name}</div>

            {tab === "market" ? (
              <div style={buttonRow}>
                <button
                  type="button"
                  style={jodiButton}
                  onClick={() => navigate(`/charts/market/${item.id}?view=jodi`)}
                >
                  Jodi Chart
                </button>
                <button
                  type="button"
                  style={panelButton}
                  onClick={() => navigate(`/charts/market/${item.id}?view=panel`)}
                >
                  Panel Chart
                </button>
              </div>
            ) : (
              <div style={buttonRow}>
                <button
                  type="button"
                  style={starlineButton}
                  onClick={() => navigate(`/charts/starline/${item.id}`)}
                >
                  Open Chart
                </button>
                <div style={countChip}>
                  <CalendarDays size={16} />
                  {item.chart_count || 0} entries
                </div>
              </div>
            )}
          </section>
        ))}

        {loading ? <div style={loadingState}>Loading charts...</div> : null}
        {!loading && !activeItems.length ? <div style={empty}>No chart data available yet.</div> : null}
      </main>
    </div>
  );
}

export default ChartsPage;

const page = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)",
  color: "#0f172a",
  fontFamily: "Inter, system-ui, sans-serif",
};
const header = {
  position: "sticky",
  top: 0,
  zIndex: 20,
  height: 66,
  background: "rgba(255,255,255,.94)",
  borderBottom: "1px solid #e2e8f0",
  display: "grid",
  gridTemplateColumns: "44px 1fr 44px",
  alignItems: "center",
  padding: "0 12px",
  backdropFilter: "blur(10px)",
};
const backButton = {
  width: 40,
  height: 40,
  borderRadius: 14,
  border: 0,
  background: "#eff6ff",
  color: "#1e3a8a",
  display: "grid",
  placeItems: "center",
};
const title = { margin: 0, textAlign: "center", fontSize: 20, fontWeight: 900 };
const headerSpacer = { width: 40, height: 40 };
const main = { maxWidth: 760, margin: "0 auto", padding: 14, display: "grid", gap: 14 };
const tabWrap = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
  background: "#eef2ff",
  padding: 8,
  borderRadius: 20,
  border: "1px solid #dbeafe",
};
const tabButton = {
  height: 50,
  border: 0,
  borderRadius: 16,
  background: "transparent",
  color: "#64748b",
  fontWeight: 800,
  fontSize: 17,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
};
const activeTab = {
  ...tabButton,
  background: "#ffffff",
  color: "#2563eb",
  boxShadow: "0 10px 24px rgba(37,99,235,.12)",
};
const card = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 24,
  padding: 16,
  boxShadow: "0 16px 30px rgba(15,23,42,.05)",
  display: "grid",
  gap: 14,
};
const marketName = { fontSize: 17, fontWeight: 900, color: "#0f172a" };
const buttonRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "center" };
const chartButtonBase = {
  minHeight: 50,
  border: 0,
  borderRadius: 16,
  color: "#fff",
  fontWeight: 900,
  fontSize: 16,
  boxShadow: "0 14px 24px rgba(37,99,235,.18)",
};
const jodiButton = {
  ...chartButtonBase,
  background: "linear-gradient(90deg,#3b82f6,#2563eb)",
};
const panelButton = {
  ...chartButtonBase,
  background: "linear-gradient(90deg,#c026d3,#9333ea)",
  boxShadow: "0 14px 24px rgba(147,51,234,.18)",
};
const starlineButton = {
  ...chartButtonBase,
  background: "linear-gradient(90deg,#0ea5e9,#2563eb)",
};
const countChip = {
  minHeight: 50,
  borderRadius: 16,
  border: "1px solid #dbeafe",
  background: "#f8fbff",
  color: "#475569",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  fontWeight: 800,
  fontSize: 14,
};
const empty = {
  background: "#fff",
  border: "1px dashed #cbd5e1",
  borderRadius: 20,
  padding: 24,
  textAlign: "center",
  color: "#64748b",
  fontWeight: 700,
};
const loadingState = {
  ...empty,
  border: "1px solid #dbeafe",
  color: "#2563eb",
  background: "linear-gradient(135deg,#ffffff,#eff6ff)",
};
